import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { platformApi } from '../api/platform';

export type UserPersona = 'civilian' | 'player' | 'geek' | 'architect';

/**
 * [账户订阅等级]
 * - FREE: 免费用户，部分模型锁定
 * - PRO: 专业版，解锁大部分模型
 * - ULTRA: 旗舰版，更大资源与并发
 * - ENTERPRISE: 企业版，全量解锁 + 优先支持
 */
export type AccountTier = 'FREE' | 'PRO' | 'ULTRA' | 'ENTERPRISE';

/**
 * [Starport 掌控等级映射]
 * 与 UI 显示的 Mastery 关联
 */
export type MasteryLevel = 'Newbie' | 'Skilled' | 'Pro' | 'Geek';

/**
 * 档位读数的来源（见 `entitlementSource` 的长注释）。
 * `platform` 目前**取不到**（后端无用户级 plan），保留取值以便将来接线时不必改类型。
 */
export type EntitlementSource = 'platform' | 'local_explicit' | 'local_default';

export const useUserStore = defineStore('user', () => {
    // [2026-10-05] 这里曾有一份 userStore 自持的主题状态（`theme` + `toggleTheme`）
    // 与一次性迁移 `theme_reset_to_light_v1`，本轮全删。三条实测理由：
    //   ① 零消费方：全仓没有任何组件/测试引用 `useUserStore().theme/toggleTheme`；
    //   ② 竞争写手：它与 omni store 各写各的键（user_theme vs omni_theme），
    //      切一次主题只有一个键动，两份读数必然漂移；
    //   ③ 迁移反规矩：`theme_reset_to_light_v1` 无条件把 user_theme 压成 light，
    //      连"在设置里选过深色"的用户也压 —— 而主题迁移的规矩（版本戳 +
    //      omni_theme_explicit 标记）已由 index.html 首屏脚本独占，且尊重显式选择。
    //   e2e/measure 里为绕它写的 theme_reset_to_light_v1 夹具行已一并移除。
    //   主题的唯一真源 = stores/omni.ts 的 setTheme（写 omni_theme + explicit +
    //   omni_theme_mode + legacy 镜像）；本 store 不再参与。

    // State
    const persona = ref<UserPersona>(localStorage.getItem('user_persona') as UserPersona || 'civilian');

    // [NEW] 账户订阅等级
    //
    // [2026-10-08 授权链订正 —— 读这一节前先读它]
    //
    // 这个值此前被当成"用户的订阅档位"用（并驱动 mastery/模型档位），但**它没有生产者**：
    // 唯一写手是 `setAccountTier`，唯一调用方是 `syncWithPlatform`，而后者**全仓 0 调用点**；
    // 而 `platformApi.getSubscription()` 当时是回读同一个 localStorage 的 mock（循环自证）。
    // ⇒ 真实桌面安装恒为 `FREE`，由它推出的 `masteryLevel` 恒为 `Newbie`。
    //
    // 【两条轴必须分清】—— 这也是本轮**推翻**原建议 B2（"客户端枚举派生自 `SubscriptionTier`"）的原因：
    //
    //   轴 A｜后端 `SubscriptionTier`（`backend/src/services/compute_metering.rs:85`）
    //        = **计算配额档**，按 `asset_id` 订阅、按 `BillingPeriod` 续期：
    //        Free 50_000 / Monthly 500_000 / Annual 2_000_000 / Enterprise u64::MAX。
    //        它回答的是"月底前还能跑多少 token"，**不是**"能用哪个模型"。
    //   轴 B｜本文件的 `AccountTier` = **界面/模型能力的展示档**。
    //        **后端没有任何用户级 plan/tier**（`users` 表无该列，全仓无写手）。
    //
    // 两者**不是同一条轴的两种拼写**，强行建映射表 = 编造一个不存在的对应关系
    // （正是本轮在修的缺陷类型）。所以这里**不建映射**；`AccountTier` 如实降格为
    // **本地能力偏好**，并通过 `entitlementSource` 把"它从哪来"暴露给所有消费方。
    //
    // 【两条相反方向的安全规则】（同一轮定的，别互相套用）
    //   · **能力可达性**（能不能看见/管理自家帐号家族）→ **不得**依赖未背书的值 ⇒ 解耦；
    //   · **付费资源**（高阶模型等）→ 未背书时**按最保守档执行**（fail-closed，宁可少给
    //     也不白送），但界面必须**明示"未校验"**，不能画成一个权威的 "FREE"。
    const accountTier = ref<AccountTier>(localStorage.getItem('account_tier') as AccountTier || 'FREE');

    /**
     * 档位读数的**来源**。任何展示/判定档位的地方都要能区分
     * "用户是 FREE" 与 "我们不知道用户是什么"（后者不是同一件事）。
     *
     * - `local_explicit`：用户在本机显式选过（`account_tier` / `mastery_override` 有值）
     * - `local_default`：什么都没有，`accountTier` 是初始占位值，**不代表用户的真实档位**
     * - `platform`：来自服务端权威（**目前永远取不到** —— 后端没有用户级 plan；
     *   等那一层真建出来再出现这个取值）
     */
    const entitlementSource = computed<EntitlementSource>(() => {
        if (typeof localStorage === 'undefined') return 'local_default';
        const explicitTier = localStorage.getItem('account_tier');
        const explicitMastery = localStorage.getItem('mastery_override');
        return (explicitTier || explicitMastery) ? 'local_explicit' : 'local_default';
    });
    /** 档位是否来自服务端权威。**恒 false** 时不要把它当"用户是免费用户"来展示。 */
    const entitlementVerified = computed(() => entitlementSource.value === 'platform');

    // [NEW] 用户 ID (用于后端验证)
    const userId = ref<string | null>(localStorage.getItem('user_id'));

    // [NEW] 维度控制器 (The Ascension Dial)
    // DEV 模式下自动解锁全部功能（与 imStore DEV mode 对齐）
    const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
    const masteryOverride = ref<MasteryLevel | null>(
        localStorage.getItem('mastery_override') as MasteryLevel | null
        || (isDev ? 'Geek' : null)
    );

    const setMasteryOverride = (level: MasteryLevel | null) => {
        masteryOverride.value = level;
        if (level) localStorage.setItem('mastery_override', level);
        else localStorage.removeItem('mastery_override');
    };

    // [Computed] 推流的自动等级
    const autoMasteryLevel = computed<MasteryLevel>(() => {
        // Web 公网模式或 DEV 模式下自动解锁全部功能（与 imStore DEV mode 对齐）
        const isTauri = typeof window !== 'undefined' && !!(window as any).__TAURI_INTERNALS__;
        if (import.meta.env.DEV || !isTauri) {
            return 'Geek';
        }
        switch (accountTier.value) {
            case 'ENTERPRISE': return 'Geek';
            case 'ULTRA': return 'Pro';
            case 'PRO': return 'Skilled';
            default: return 'Newbie';
        }
    });

    // [Computed] 最终判定的掌控等级，优先采纳手写干涉
    const masteryLevel = computed<MasteryLevel>(() => masteryOverride.value || autoMasteryLevel.value);

    // [Computed] 是否可以访问高分模型 (phys_score > 90)
    const canAccessPremiumModels = computed(() => {
        return accountTier.value !== 'FREE';
    });

    // [Computed] 最大可下载模型尺寸 (GB)
    const maxModelSizeGB = computed(() => {
        switch (accountTier.value) {
            case 'ENTERPRISE': return Infinity;
            case 'ULTRA': return 500;
            case 'PRO': return 100;
            default: return 20;
        }
    });

    // Actions
    const setPersona = (newPersona: UserPersona) => {
        console.log('[UserStore] Switching Persona:', newPersona);
        persona.value = newPersona;
        localStorage.setItem('user_persona', newPersona);

        // Persona-based side effects (optional e.g. change theme)
        if (newPersona === 'player') {
            document.documentElement.classList.add('mode-gaming');
        } else {
            document.documentElement.classList.remove('mode-gaming');
        }
    };

    // [NEW] 设置账户等级
    const setAccountTier = (newTier: AccountTier) => {
        console.log('[UserStore] Upgrading Account Tier:', newTier);
        accountTier.value = newTier;
        localStorage.setItem('account_tier', newTier);
    };

    // [NEW] 模拟登录/绑定用户
    const setUserId = (id: string | null) => {
        userId.value = id;
        if (id) {
            localStorage.setItem('user_id', id);
        } else {
            localStorage.removeItem('user_id');
        }
    };

    // [NEW] Actions for Platform Sync
    const syncWithPlatform = async () => {
        try {
            console.log('[UserStore] Syncing with Operations Platform...');
            const profile = await platformApi.getUserProfile();
            const subscription = await platformApi.getSubscription();

            // Sync Local State
            setUserId(profile.id);
            setAccountTier(subscription.tier);

            return { profile, subscription };
        } catch (e) {
            console.warn('[UserStore] Platform sync failed, using local cache:', e);
            return null; // Fallback to local
        }
    };

    const upgradeSubscription = async (tier: AccountTier, paymentMethod: 'stripe' | 'alipay' | 'wechat') => {
        try {
            const intent = await platformApi.upgradeSubscription(tier, paymentMethod);
            if (intent.stripeSessionId || intent.alipayQrCode || intent.wechatQrCode) {
                return intent;
            }
        } catch (e) {
            console.error('[UserStore] Upgrade failed:', e);
            throw e;
        }
    };

    if (persona.value === 'player') document.documentElement.classList.add('mode-gaming');

    return {
        persona,
        accountTier,
        entitlementSource,
        entitlementVerified,
        userId,
        masteryLevel,
        canAccessPremiumModels,
        maxModelSizeGB,
        setPersona,
        setAccountTier,
        setUserId,
        syncWithPlatform,
        upgradeSubscription,
        setMasteryOverride
    };
});

